'use client'

import { type ComponentPropsWithoutRef, useEffect, useMemo, useState } from 'react'
import clsx from 'clsx'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import qs from 'qs'
import { pathOr } from 'ramda'

import {
  Brand,
  BRAND_SORTING_OPTIONS,
  CategoryTags,
  getSelectValueBySortingOptions,
  getSortingOptionsBySelectValueForBrand,
  ListingCategoryType,
  parseQueryFilters,
  SortingOptionsTypes,
  useInfiniteScroll
} from 'modules/products'
import BackToTopButton from 'shared/ui/BackToTopButton'
import Breadcrumbs from 'shared/ui/Breadcrumbs'
import SelectDropdown from 'shared/ui/form/Select'
import InfiniteScrollAnchor from 'shared/ui/InfiniteScrollAnchor'
import LoadingIndicator from 'shared/ui/LoadingIndicator/LoadingIndicator'
import { PRODUCTS_LISTING_LIMIT } from 'shared/utils/common'
import { PATHS } from 'shared/utils/paths'
import { isNotNilOrEmpty } from 'shared/utils/ramda'
import { addTestId } from 'shared/utils/tests'

import { getBrandsDictionaryNextPage } from '../api'

import styles from './AllBrandsPage.module.scss'

interface WytworcyClientProps {
  token: string
  brands: Brand[]
  categories: ListingCategoryType[]
  searchParams: { [key: string]: string | string[] | undefined }
}

export default function AllBrandsPage({
  token,
  brands,
  categories: categoriesData,
  searchParams
}: WytworcyClientProps) {
  const router = useRouter()
  const [sorting, setSorting] = useState<string | string[]>('recommended')
  const [category, setCategory] = useState<ListingCategoryType | null>(null)
  const categories = useMemo(() => categoriesData || [], [categoriesData])
  const {
    productsData: brandsData,
    isFetching,
    ref
  } = useInfiniteScroll({
    products: brands,
    query: searchParams,
    token,
    slug: '',
    getNextPageCallback: getBrandsDictionaryNextPage,
    limit: PRODUCTS_LISTING_LIMIT
  })

  const handleSortingChange = async (_, value: string | string[]) => {
    setSorting(value)

    const sortingOptions = getSortingOptionsBySelectValueForBrand(value)
    const parsedQuery = parseQueryFilters(searchParams)

    if (value === 'recommended') {
      delete parsedQuery?.order
      delete parsedQuery?.sort_by
    }

    const params = qs.stringify(
      { ...parsedQuery, ...sortingOptions },
      { addQueryPrefix: true }
    )

    router.push(`/wytworcy${decodeURIComponent(params)}`)
  }

  const handleCategoryChange = async (_, value: string | string[]) => {
    const parsedQuery = parseQueryFilters(searchParams)
    let params = ''

    if (value === category?.slug) {
      setCategory(null)
      delete parsedQuery?.category
      params = qs.stringify(parsedQuery, { addQueryPrefix: true })
    } else {
      const selectedCategory = categories.find(cat => cat.slug === value)

      setCategory(selectedCategory || null)
      params = qs.stringify(
        { ...parsedQuery, category: value },
        { addQueryPrefix: true }
      )
    }

    router.push(`/wytworcy${decodeURIComponent(params)}`)
  }

  useEffect(() => {
    const sortingToCheck = {
      sort_by: searchParams.sort_by,
      order: searchParams.order
    } as SortingOptionsTypes

    const sortingName = getSelectValueBySortingOptions(sortingToCheck)

    if (sortingName !== sorting) {
      setSorting(sortingName)
    }

    if (searchParams.category) {
      setCategory(
        categories.find(
          categoryItem => categoryItem.slug === searchParams.category
        ) || null
      )
    }
  }, [searchParams, categories, sorting])

  const breadcrumbs = [{ label: 'Wszyscy wytwórcy', path: PATHS.brand }]

  return (
    <>
      <div className={styles.breadcrumbsWrapper}>
        <Breadcrumbs breadcrumbs={breadcrumbs} />
      </div>
      <div className={styles.header}>
        <div className={styles.titleBanner}>
          <h1 className={styles.titleBannerHeading}>Nasi wytwórcy</h1>
        </div>
      </div>
      <div className={styles.onlyWebContent}>
        <CategoryTags
          categories={categories.map(categoryItem => ({
            ...categoryItem,
            value: categoryItem.slug
          }))}
          mainCategory={category}
          onClick={handleCategoryChange}
        />
      </div>
      <div className={styles.header}>
        <div className={styles.onlyDesktopContent}>
          <h2 className={styles.title}>Wytwórcy Lokalnego Rolnika</h2>
        </div>
        <SortingInputWrapper {...addTestId('sorting-input')}>
          <SortingLabel>Sortuj:</SortingLabel>
          <SelectDropdown
            id='brands-page-sorting'
            variant='ghost'
            noMargin
            options={BRAND_SORTING_OPTIONS}
            name='sorting'
            value={sorting}
            onChange={handleSortingChange}
          />
        </SortingInputWrapper>
      </div>
      <span data-title='top' />
      <div className={styles.wrapper}>
        <BackToTopButton target='*[data-title="top"]' />
        {isNotNilOrEmpty(brandsData) &&
          brandsData.map(brand => {
            const brandLogo = pathOr(
              '/static/photo-placeholder.svg',
              ['brandLogo'],
              brand
            )

            return (
              <Link
                href={`/wytworcy/${
                  typeof brand.slug === 'string'
                    ? brand.slug
                    : (brand.slug?.slug ?? '')
                }`}
                key={brand.id}
              >
                <div className={styles.brandWrapper} key={brand.id}>
                  <div className={styles.brandLogoWrapper}>
                    <Image
                      src={brandLogo}
                      alt={brand.name}
                      fill
                      style={{ objectFit: 'contain' }}
                    />
                  </div>

                  <h3 className={styles.brandName}>{brand.name}</h3>
                </div>
              </Link>
            )
          })}
        <InfiniteScrollAnchor ref={ref} />
      </div>
      {isFetching && (
        <div className={styles.loaderWrapper}>
          <LoadingIndicator />
        </div>
      )}
    </>
  )
}

export const SortingInputWrapper = ({
  className,
  ...rest
}: ComponentPropsWithoutRef<'div'>) => (
  <div className={clsx(styles.sortingInputWrapper, className)} {...rest} />
)

export const SortingLabel = ({
  className,
  ...rest
}: ComponentPropsWithoutRef<'div'>) => (
  <div className={clsx(styles.sortingLabel, className)} {...rest} />
)
